import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faSync,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { analyticsAPI } from "../../services/analyticsService";
import Loader from "../Loader";
import Styles from "../../styles/Analytics.module.css";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "../../utils/analyticsUtils";

const PAGE_SIZE = 50;

const USER_FILTERS = [
  { value: "all", label: "All users" },
  { value: "never_subscribed", label: "Never subscribed / normal" },
  { value: "trialed_left", label: "Trialed, then left" },
  { value: "lapsed_paid", label: "Previously paid, now inactive" },
  { value: "active_paid", label: "Active subscribers" },
  { value: "monthly", label: "Monthly subscribers" },
  { value: "annual", label: "Annual subscribers" },
  { value: "trial", label: "Active trial users" },
  { value: "active_app", label: "Active app users (30d)" },
  { value: "inactive_app", label: "Inactive app users" },
  { value: "referred", label: "Referred users" },
  { value: "affiliate", label: "Affiliates / referrers" },
];

const getFilterLabel = (value) =>
  USER_FILTERS.find((option) => option.value === value)?.label || "All users";

const hasCurrentEntitlement = (row) => {
  const isTrial =
    row.subscription_is_trial ||
    String(row.subscription_status_current || "").toLowerCase() === "trial";
  const expiry = isTrial
    ? row.subscription_trial_ends_at_current
    : row.subscription_expires_at_current;
  return !expiry || new Date(expiry).getTime() > Date.now();
};

const subscriptionTone = (row) => {
  const status = String(row.subscription_status_current || "").toLowerCase();
  const isTrial = row.subscription_is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(row)) return Styles.statusTrial;
  if (status === "active" && !isTrial && hasCurrentEntitlement(row)) {
    return Styles.statusPro;
  }
  return Styles.statusFree;
};

const subscriptionLabel = (row) => {
  const status = String(row.subscription_status_current || "").toLowerCase();
  const isTrial = row.subscription_is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(row)) return "Trial";
  if (status === "active" && !isTrial && hasCurrentEntitlement(row)) {
    return row.subscription_plan_current === "yearly" ? "Annual" : "Monthly";
  }
  return status === "active" ? "Expired" : "Free";
};

const AnalyticsUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedFilter = searchParams.get("filter") || "all";
  const filter = USER_FILTERS.some((option) => option.value === requestedFilter)
    ? requestedFilter
    : "all";
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    rows: [],
    total: 0,
    limit: PAGE_SIZE,
    segment_counts: {},
  });
  const requestId = useRef(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const setFilter = (nextFilter) => {
    setPage(0);
    setSearchParams(
      nextFilter === "all" ? {} : { filter: nextFilter },
      { replace: true },
    );
  };

  const load = useCallback(async () => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    setLoading(true);
    setError("");
    setData({ rows: [], total: 0, limit: PAGE_SIZE, segment_counts: {} });

    try {
      const result = await analyticsAPI.getUsers({
        search: query,
        filter,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (requestId.current === currentRequest) {
        setData({
          rows: result?.rows || [],
          total: Number(result?.total) || 0,
          segment_counts: result?.segment_counts || {},
          limit: Number(result?.limit) || PAGE_SIZE,
        });
      }
    } catch (loadError) {
      if (requestId.current === currentRequest) {
        setError(loadError.message || "Failed to load user analytics");
      }
    } finally {
      if (requestId.current === currentRequest) {
        setLoading(false);
      }
    }
  }, [filter, page, query]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const firstResult = data.total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastResult = Math.min((page + 1) * PAGE_SIZE, data.total);

  const openUser = (userId) => navigate(`/analytics/users/${userId}`);

  const handleRowClick = (event, userId) => {
    if (event.target.closest("a, button")) return;
    openUser(userId);
  };

  const handleRowKeyDown = (event, userId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openUser(userId);
    }
  };

  if (loading && data.rows.length === 0) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading user analytics" />
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.headerRow}>
        <div>
          <h1 className={Styles.title}>User Analytics</h1>
          <p className={Styles.welcomeMessage}>
            Search every account and open a user to inspect identity,
            subscriptions, app activity, referrer, and referred users.
          </p>
        </div>
        <div className={Styles.headerActions}>
          <button
            type="button"
            className={Styles.refreshButton}
            onClick={load}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            {loading ? "Refreshing" : "Refresh"}
          </button>
          <button
            type="button"
            className={Styles.backButton}
            onClick={() => navigate("/analytics")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Analytics
          </button>
        </div>
      </div>

      {error && (
        <div className={Styles.errorBox} role="alert">
          {error}
        </div>
      )}

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>{getFilterLabel(filter)}</h2>
          <p className={Styles.sectionSubtitle}>
            {formatNumber(data.total)} matching accounts
          </p>
        </div>
        <div className={Styles.headerActions}>
          <select
            className={Styles.rangeSelect}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            aria-label="Filter users by account type"
          >
            {USER_FILTERS.map((option) => {
              const count = data.segment_counts?.[option.value];
              return (
                <option key={option.value} value={option.value}>
                  {count == null
                    ? option.label
                    : `${option.label} (${formatNumber(count)})`}
                </option>
              );
            })}
          </select>
          <label className={Styles.searchLabel}>
            <FontAwesomeIcon icon={faUsers} />
            <input
              className={Styles.searchInput}
              placeholder="Search email, username, or code"
              aria-label="Search users"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Subscription</th>
                <th>Referred by</th>
                <th>Referrer email</th>
                <th>Code</th>
                <th>Referrals</th>
                <th>Locations</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((user) => (
                <tr
                  key={user.id}
                  className={Styles.clickableRow}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open analytics for @${user.username}`}
                  onClick={(event) => handleRowClick(event, user.id)}
                  onKeyDown={(event) => handleRowKeyDown(event, user.id)}
                >
                  <td>
                    <Link
                      className={Styles.userLink}
                      to={`/analytics/users/${user.id}`}
                    >
                      @{user.username}
                    </Link>
                    <span className={Styles.topMeta}>{user.role}</span>
                    {user.is_affiliate && (
                      <span className={`${Styles.statusBadge} ${Styles.statusTrial}`}>
                        Affiliate
                      </span>
                    )}
                  </td>
                  <td>
                    <Link
                      className={Styles.emailLink}
                      to={`/analytics/users/${user.id}`}
                    >
                      {user.email}
                    </Link>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <span
                      className={`${Styles.statusBadge} ${subscriptionTone(user)}`}
                    >
                      {subscriptionLabel(user)}
                    </span>
                    {user.subscription_price_paid != null && (
                      <span className={Styles.topMeta}>
                        {formatMoney(
                          user.subscription_price_paid,
                          user.subscription_currency || "USD",
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    {user.referrer_id ? (
                      <Link
                        className={Styles.userLink}
                        to={`/analytics/users/${user.referrer_id}`}
                      >
                        @{user.referrer_username}
                      </Link>
                    ) : (
                      <span className={Styles.topMeta}>Organic</span>
                    )}
                    {user.referrer_is_affiliate && (
                      <span className={`${Styles.statusBadge} ${Styles.statusTrial}`}>
                        Affiliate
                      </span>
                    )}
                  </td>
                  <td>
                    {user.referrer_email ? (
                      <Link
                        className={Styles.emailLink}
                        to={`/analytics/users/${user.referrer_id}`}
                      >
                        {user.referrer_email}
                      </Link>
                    ) : (
                      <span className={Styles.topMeta}>—</span>
                    )}
                  </td>
                  <td>{user.referral_code || "—"}</td>
                  <td>
                    {formatNumber(
                      user.actual_referral_count ?? user.referral_count,
                    )}
                    {user.paid_referral_count > 0 && (
                      <span className={Styles.topMeta}>
                        {formatNumber(user.paid_referral_count)} paid
                      </span>
                    )}
                  </td>
                  <td>{formatNumber(user.locations)}</td>
                  <td>{formatDateTime(user.last_activity_at)}</td>
                </tr>
              ))}
              {!loading && data.rows.length === 0 && (
                <tr>
                  <td colSpan="10" className={Styles.emptyTableCell}>
                    No users match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={Styles.pagination}>
          <span>
            Showing {firstResult}–{lastResult} of {formatNumber(data.total)}
          </span>
          <div className={Styles.paginationActions}>
            <button
              type="button"
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              disabled={page === 0 || loading}
              aria-label="Previous page"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span>
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((value) => value + 1)}
              disabled={page + 1 >= totalPages || loading}
              aria-label="Next page"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsUsers;
